import React, { useState } from 'react';
import './UpdateCredentialsModal.css';
import PasswordField from './PasswordField';

// Profile → Change email: the web's counterpart of the phone's ChangeEmailModal, in the same two steps
// (server/utils/emailChange.js has the flow and why). The new address and your current password first;
// a 6-digit code goes to the NEW address; with the code the email changes and the old address is told.
// The address is where password reset codes go, so a typo must not be able to lose the account.
const post = async (path, body) => {
  const response = await fetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  return { ok: response.ok, data };
};

const ChangeEmailModal = ({ onClose, userData, addNotification, onChanged }) => {
  const [step, setStep] = useState('form'); // 'form' | 'code'
  const [newEmail, setNewEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const sendCode = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const { ok, data } = await post('/api/users/me/email/request', { newEmail: newEmail.trim(), currentPassword: password });
      if (!ok) { setError(data.message || 'The email could not be changed.'); return; }
      setSentTo(data.sentTo || newEmail.trim());
      setStep('code');
    } catch (_) {
      setError('No connection. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const { ok, data } = await post('/api/users/me/email/confirm', { code });
      if (!ok) { setError(data.message || 'That code is wrong or has expired.'); return; }
      addNotification?.(`Your account now uses ${data.email}.`, 'green');
      onChanged?.(data.email);
      onClose();
    } catch (_) {
      setError('No connection. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const errorLine = error && <p role="alert" style={{ color: 'var(--danger, #c62828)', margin: 0 }}>{error}</p>;

  return (
    <div className="update-credentials-modal-overlay">
      <div className="update-credentials-modal-content">
        <div className="update-credentials-modal-header">
          <h2>Change email</h2>
          <button className="update-credentials-modal-close-button" onClick={onClose} aria-label="Close">&times;</button>
        </div>
        <div className="update-credentials-modal-body">
          {step === 'form' ? (
            <form onSubmit={sendCode}>
              <p style={{ margin: 0 }}>
                Now: <strong>{userData?.email || 'none'}</strong><br />
                We'll email a code to the new address to make sure it's yours.
              </p>
              <div className="field">
                <label className="field-label" htmlFor="new-email">New email</label>
                <input className="control" type="email" id="new-email" autoComplete="email"
                  value={newEmail} onChange={(e) => setNewEmail(e.target.value)} required />
              </div>
              <div className="field">
                <label className="field-label" htmlFor="email-current-password">Current password</label>
                <PasswordField className="control" id="email-current-password" autoComplete="current-password"
                  value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
              {errorLine}
              <div className="btn-row">
                <button type="submit" className="btn btn--primary" disabled={busy}>{busy ? 'Sending…' : 'Send code'}</button>
                <button type="button" className="btn btn--secondary" onClick={onClose}>Cancel</button>
              </div>
            </form>
          ) : (
            <form onSubmit={confirm}>
              <p style={{ margin: 0 }}>We sent a 6-digit code to <strong>{sentTo}</strong>. It expires in 15 minutes.</p>
              <div className="field">
                <label className="field-label" htmlFor="email-code">Code</label>
                <input className="control" id="email-code" inputMode="numeric" autoComplete="one-time-code" maxLength={7}
                  value={code} onChange={(e) => setCode(e.target.value)} required
                  style={{ fontSize: '1.3rem', letterSpacing: '0.3em', textAlign: 'center' }} />
              </div>
              {errorLine}
              <div className="btn-row">
                <button type="submit" className="btn btn--primary" disabled={busy}>{busy ? 'Changing…' : 'Change email'}</button>
                <button type="button" className="btn btn--secondary" onClick={() => { setError(''); setStep('form'); }}>Back</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangeEmailModal;
